import { Box, Text } from "@mantine/core";
import { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { TenantPill } from "./components/atoms";
import { IAuth } from "./interfaces/IAuth";
import { markBootChecked } from "./components/layout/session-expired";
import { bootMe } from "./boot-me";
import { SwitchOverlay } from "./components/layout/switch-overlay";
import { authChange, authMe } from "./store/auth";
import { persistor } from "./store";
import classes from "./states.module.css";
import { lazyPage } from "./utils/lazy-page";

// One chunk per page: a signed-in user no longer downloads the login, DAN and
// recovery screens (and their CSS) before the dashboard can paint. lazyPage
// reloads once when a deploy has removed the chunk an open tab asks for.
const EmailVerify = lazyPage(() => import("./pages/auth/email-verify"));
const GateHubCallback = lazyPage(() => import("./pages/auth/gate-hub"));
const ForgotPassword = lazyPage(() => import("./pages/auth/forgot"));
const Login = lazyPage(() => import("./pages/auth/login"));
const Verify = lazyPage(() => import("./pages/auth/verify").then((m) => ({ default: m.Verify })));
const Businesses = lazyPage(() => import("./pages/businesses"));
const HomePage = lazyPage(() => import("./pages/main/index"));
const NotificationsPage = lazyPage(() => import("./pages/notifications"));
const PrivacyPage = lazyPage(() => import("./pages/privacy"));
const Profile = lazyPage(() => import("./pages/profile"));

// Canvas 3.6 «Апп нээгдэх үед»: tenant pill + progress while /me checks the
// stored session.
function BootScreen() {
  const { t } = useTranslation("profileForm");

  return (
    <Box className={classes.screen}>
      <Box role="status" aria-live="polite" className={classes.boot}>
        <TenantPill size="md" />
        <Box className={classes.track} aria-hidden="true">
          <Box className={classes.bar} />
        </Box>
        <Text className={classes.bootText}>{t("states.bootChecking")}</Text>
      </Box>
    </Box>
  );
}

// Half-way through PIN recovery the session has no business, so the home page
// would wait forever (F23). Such a session only gets the profile page, where
// the recovery dialog opens on the step the scope points to.
const PIN_RECOVERY_SCOPES = ["FORGOT_PIN", "RESET_PIN"];

type ConditionalAccessProps = Readonly<{
  children: React.ReactNode;
  accessToken: string | null;
  sessionScope: string | null;
}>;

const ConditionalAccess = ({ children, accessToken, sessionScope }: ConditionalAccessProps) => {
  const [searchParams] = useSearchParams();

  const hasQueryParams = Array.from(searchParams.keys()).length > 0;

  if (hasQueryParams) {
    return children;
  }

  // Allow FORGOT_PIN and RESET_PIN for PIN recovery flow on profile page
  const allowedScopes = ["AUTHORIZED", "RESTRICTED_SESSION", "FORGOT_PIN", "RESET_PIN"];
  if (accessToken && allowedScopes.includes(sessionScope || "")) {
    return children;
  }

  return <Navigate to="/login" />;
};

export function App() {
  const accessToken = useSelector((state: { auth: IAuth }) => state.auth.accessToken);
  const sessionScope = useSelector((state: { auth: IAuth }) => state.auth.sessionScope);

  const dispatch = useDispatch();

  const isLoggingOutParam = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("logout");
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(isLoggingOutParam);

  useEffect(() => {
    if (!isLoggingOut) return;
    dispatch(authChange());
    persistor.purge();
    const params = new URLSearchParams(window.location.search);
    params.delete("logout");
    const qs = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    setIsLoggingOut(false);
  }, [isLoggingOut, dispatch]);

  // Boot дээр persisted token-ийг ДАШБОРД mount хийхээс ӨМНӨ баталгаажуулна.
  // Өөр модулиас (өөр origin/localStorage) гарахад server session устдаг ч
  // auth-admin-ийн өөрийн persisted token хэвээр үлддэг. Хэрэв шалгалгүйгээр
  // шууд dashboard-ийг mount хийвэл бүх endpoint хуучин token-оор 401 буцааж
  // "баахан алдаа" гарна. me() амжилттай бол л authenticated route рендэрлэнэ.
  const needsValidation =
    !isLoggingOut &&
    !!accessToken &&
    // PIN-recovery tokens expire too (30 min); /me accepts every scope.
    ["AUTHORIZED", "RESTRICTED_SESSION", ...PIN_RECOVERY_SCOPES].includes(sessionScope || "");
  const [validating, setValidating] = useState<boolean>(needsValidation);

  useEffect(() => {
    if (!needsValidation) {
      markBootChecked();
      setValidating(false);
      return;
    }
    let cancelled = false;
    // Usually already in flight: index.tsx starts it alongside /theme/resolve (boot-me.ts).
    bootMe()
      .then((auth) => {
        if (!cancelled) dispatch(authMe(auth));
      })
      .catch(() => {
        // me() 401 → errorHandler (utils/index.ts) /2fa/auth замд session-ийг
        // authChange()-ээр аль хэдийн цэвэрлэдэг тул доош login рендэрлэгдэнэ.
      })
      .finally(() => {
        // After the boot 401 (if any) has cleared the session, later 401s may open the dialog.
        markBootChecked();
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
    // Зөвхөн boot дээр нэг удаа ажиллана.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (validating) return <BootScreen />;

  let authRoutes = (
    <>
      <Route path="/" element={<HomePage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="*" element={<Navigate to="/" />} />
    </>
  );

  if (sessionScope === "RESTRICTED_SESSION") {
    authRoutes = (
      <>
        <Route path="/businesses" element={<Businesses />} />
        <Route path="*" element={<Navigate to="/businesses" />} />
      </>
    );
  }

  if (PIN_RECOVERY_SCOPES.includes(sessionScope || "")) {
    authRoutes = <Route path="*" element={<Navigate to="/auth/profile/dan" replace />} />;
  }

  return (
    <>
      <SwitchOverlay />
      {/* While a page's chunk loads: the same screen as the boot check. */}
      <Suspense fallback={<BootScreen />}>
        <Routes>
          <Route
            path="/auth/profile/dan"
            element={
              <ConditionalAccess accessToken={accessToken} sessionScope={sessionScope}>
                <Profile />
              </ConditionalAccess>
            }
          />
          {/* Public — accessible regardless of auth state */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/auth/gate-hub" element={<GateHubCallback />} />
          {!isLoggingOut && accessToken && ["AUTHORIZED", "RESTRICTED_SESSION", "FORGOT_PIN", "RESET_PIN"].includes(sessionScope || "") ? (
            authRoutes
          ) : (
            <>
              <Route path="/login" element={<Login />} />
              <Route path="/registration/:id" element={<EmailVerify />} />
              <Route path="/verify/:id" element={<Verify />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="*" element={<Navigate to="/login" />} />
            </>
          )}
        </Routes>
      </Suspense>
    </>
  );
}
